import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Minus, Plus, CreditCard } from "lucide-react";
import { formatCOP } from "@/lib/api";

const priceLabel = (n) => formatCOP(n);

export default function BuyNowDialog({ book, open, onOpenChange, onConfirm, shippingFee = 0, initialQty = 1 }) {
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (open) setQty(initialQty || 1);
  }, [open, initialQty]);

  if (!book) return null;

  const tracked = typeof book.stock === "number";
  const outOfStock = tracked && book.stock <= 0;
  const maxQty = tracked && book.stock > 0 ? Math.min(20, book.stock) : 20;
  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(maxQty, q + 1));
  const subtotal = book.price * qty;
  const total = subtotal + shippingFee;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="buy-now-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl text-[var(--lc-ink)]">
            Confirmar compra
          </DialogTitle>
          <DialogDescription className="text-[var(--lc-slate)]">
            {outOfStock
              ? "Este libro está agotado. Puedes encargarlo y lo conseguiremos para ti."
              : "Elige cuántas unidades quieres comprar de este libro."}
          </DialogDescription>
        </DialogHeader>

        {outOfStock && (
          <div className="rounded-xl bg-[var(--lc-accent)]/10 text-[var(--lc-accent)] text-sm font-semibold px-4 py-2.5 -mt-1" data-testid="buy-now-preorder-note">
            Encargo: tu pago queda registrado y el administrador conseguirá el libro.
          </div>
        )}

        <div className="flex gap-4 mt-1">
          <img
            src={book.image_url}
            alt={`Portada de ${book.title}`}
            className="w-24 h-36 object-cover object-top rounded-xl bg-[var(--lc-cream)] shrink-0 shadow"
          />
          <div className="flex flex-col min-w-0">
            <h3 className="font-display font-bold text-lg text-[var(--lc-ink)] leading-tight" data-testid="buy-now-title">
              {book.title}
            </h3>
            <p className="text-sm text-[var(--lc-slate)] mt-0.5">{book.author}</p>
            {book.publisher && (
              <p className="text-xs text-[var(--lc-slate)]/80 mt-0.5">Editorial: {book.publisher}</p>
            )}
            <p className="font-display font-bold text-lg text-[var(--lc-ink)] mt-2">
              {priceLabel(book.price)}
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <span className="font-bold text-[var(--lc-ink)]">Cantidad</span>
          <div className="flex items-center gap-3">
            <button
              onClick={dec}
              disabled={qty <= 1}
              data-testid="buy-now-decrease"
              className="w-9 h-9 grid place-items-center rounded-full border-2 border-[var(--lc-border)] text-[var(--lc-ink)] hover:border-[var(--lc-primary)] hover:text-[var(--lc-primary)] disabled:opacity-40 transition-colors"
              aria-label="Disminuir cantidad"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-8 text-center font-display font-black text-xl text-[var(--lc-ink)]" data-testid="buy-now-qty">
              {qty}
            </span>
            <button
              onClick={inc}
              disabled={qty >= maxQty}
              data-testid="buy-now-increase"
              className="w-9 h-9 grid place-items-center rounded-full border-2 border-[var(--lc-border)] text-[var(--lc-ink)] hover:border-[var(--lc-primary)] hover:text-[var(--lc-primary)] disabled:opacity-40 transition-colors"
              aria-label="Aumentar cantidad"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-[#F0F9FF] p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--lc-slate)] font-semibold">Subtotal</span>
            <span className="font-semibold text-[var(--lc-ink)]" data-testid="buy-now-subtotal">{priceLabel(subtotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--lc-slate)] font-semibold">Envío</span>
            <span className="font-semibold text-[var(--lc-ink)]" data-testid="buy-now-shipping">{priceLabel(shippingFee)}</span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[var(--lc-border)]">
            <span className="font-semibold text-[var(--lc-slate)]">Total</span>
            <span className="font-display font-black text-2xl text-[var(--lc-ink)]" data-testid="buy-now-total">
              {priceLabel(total)}
            </span>
          </div>
        </div>

        <button
          onClick={() => onConfirm(qty)}
          data-testid="buy-now-confirm"
          className={`mt-2 w-full flex items-center justify-center gap-2 rounded-full text-white py-4 font-display font-extrabold text-lg transition-colors shadow-[0_10px_20px_-4px_rgba(225,70,90,0.5)] ${
            outOfStock ? "bg-[var(--lc-accent)] hover:opacity-90" : "bg-[var(--lc-primary)] hover:bg-[var(--lc-primary-hover)]"
          }`}
        >
          <CreditCard className="w-5 h-5" />
          {outOfStock ? "Encargar y pagar" : "Ir a pagar"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
